<template>
  <v-app ref="appcontainer" class="lightBg is-relative overflow-clip test-class">
    <div id="app-notifications" :class="{ 'is-mobile': false }">
      <app-notification
        position="bottom right"
        class="mb-2"
        group="bottom-notifications"
        :max="5"></app-notification>
      <app-notification
        position="top right"
        class="mt-2 mr-2"
        group="top-notifications"
        :max="5"></app-notification>
    </div>
    <navigation-drawer
      v-if="!$route.meta.hideSidebar && !shouldRenderMobile"
      :unread-msg-count="unreadMsgCount"
      @supportBtnClick="_openChatWindow"
      :orgRequired="orgRequired"></navigation-drawer>

    <header-mobile v-if="hasUser && shouldRenderMobile"></header-mobile>

    <v-main :class="kebabCase($route.name)">
      <v-alert type="error" v-if="showDisabledOrgAlert" prominent text>
        You are on View Only mode because your Org is inactive. Contact our team to request
        activation.
      </v-alert>
      <feature-flag name="enable-maintenance-mode">
        <template #enabled>
          <maintenance-banner v-if="!shouldRenderMobile"></maintenance-banner>
        </template>
      </feature-flag>
      <v-container
        fluid
        :class="{
          'viewport-height': orgRequired,
          'px-6 py-4': !$route.meta.fullScreen && !shouldRenderMobile,
          'pa-0 full-height': $route.meta.fullScreen || shouldRenderMobile
        }"
        v-if="$appLoaded">
        <h1 v-if="!orgRequired && !$route.meta.customHeader && !isMobile" class="mb-6">
          {{ $pageTitle || getRouteTitle($route.meta.title) }}
        </h1>
        <transition name="fade" mode="out-in">
          <div :class="{ 'page-wrapper': !shouldRenderMobile, 'full-height': shouldRenderMobile }">
            <template v-if="!shouldRenderMobile">
              <new-org-welcome v-if="orgRequired"></new-org-welcome>
            </template>

            <not-ready-message v-if="isMobile && !$route.meta.allowMobile"></not-ready-message>

            <router-view
              v-if="!orgRequired && ((isMobile && $route.meta.allowMobile) || !isMobile)"
              :class="{ 'is-mobile-content': shouldRenderMobile }"></router-view>
          </div>
        </transition>
      </v-container>
    </v-main>
    <new-terms :is-confirmation-needed="isConfirmationNeeded"></new-terms>
    <event-triggered-lazy-dialog-host
      event-name="open-remove-carrier-contact-dialog"
      :component="EventTriggeredRemoveCarrierContactDialog" />
    <event-triggered-lazy-dialog-host
      event-name="open-create-carrier-company-dialog"
      :component="EventTriggeredCreateCarrierCompanyDialog" />
    <event-triggered-lazy-dialog-host
      event-name="open-create-carrier-contact-dialog"
      :component="EventTriggeredCreateCarrierContactDialog" />

    <feature-flag name="enable-yard-tasks">
      <template #enabled>
        <event-triggered-lazy-dialog-host
          event-name="open-create-yard-task-drawer"
          :component="EventTriggeredCreateTaskDrawer" />
        <event-triggered-lazy-dialog-host
          event-name="open-edit-yard-task-drawer"
          :component="EventTriggeredEditTaskDrawer" />
      </template>
    </feature-flag>

    <feature-flag name="enable-yard-move-task-interactive-creation">
      <template #enabled>
        <event-triggered-lazy-dialog-host
          event-name="open-create-yard-move-task-drawer"
          :component="EventTriggeredCreateMoveTaskDrawer" />
      </template>
    </feature-flag>

    <event-triggered-lazy-dialog-host
      event-name="open-limits-overview-drawer"
      :component="EventTriggeredLimitsOverviewDrawer" />
  </v-app>
</template>

<script>
import { DateTime } from 'luxon';
import { mapState } from 'vuex';
import config from '@/config/config';
import { FeatureFlag } from '@loadsmart/vue-feature-flags';

import EventTriggeredLazyDialogHost from '@satellite/components/elements/dialogs/EventTriggeredLazyDialogHost.vue';
import monitorWebsocketConnectionMixin from '@satellite/components/mixins/monitorWebsocketConnectionMixin';
import { getRouteTitle, isMobile } from '@satellite/plugins/util';
import { kebabCase } from 'lodash';
import { useAuth, useMixpanel, useSettings } from '@/composables/index.js';
import { useI18n } from 'vue-i18n';
import { defineAsyncComponent, markRaw, watch } from 'vue';
import { useLocale } from 'vuetify';
import { useLocaleHelpers } from '@satellite/composables/useLocaleHelpers.js';
import routeHelpers from '@satellite/helpers/routes.js';
import { EntitySettings, isInternalUser, isLoggedInAsAnotherUser } from '@nova/core';

const EventTriggeredRemoveCarrierContactDialog = markRaw(
  defineAsyncComponent(() =>
    import('./modules/carrier_contacts/components/EventTriggeredRemoveCarrierContactDialog.vue')
  )
);
const EventTriggeredCreateCarrierCompanyDialog = markRaw(
  defineAsyncComponent(() =>
    import('./modules/carriers/components/EventTriggeredCreateCarrierCompanyDialog.vue')
  )
);
const EventTriggeredCreateCarrierContactDialog = markRaw(
  defineAsyncComponent(() =>
    import('./modules/carriers/components/EventTriggeredCreateCarrierContactDialog.vue')
  )
);
const EventTriggeredCreateMoveTaskDrawer = markRaw(
  defineAsyncComponent(() =>
    import('./modules/yard/components/EventTriggeredCreateMoveTaskDrawer.vue')
  )
);
const EventTriggeredCreateTaskDrawer = markRaw(
  defineAsyncComponent(() => import('./modules/yard/components/EventTriggeredCreateTaskDrawer.vue'))
);
const EventTriggeredEditTaskDrawer = markRaw(
  defineAsyncComponent(() => import('./modules/yard/components/EventTriggeredEditTaskDrawer.vue'))
);
const EventTriggeredLimitsOverviewDrawer = markRaw(
  defineAsyncComponent(() =>
    import('./modules/appointments/components/limits/EventTriggeredLimitsOverviewDrawer.vue')
  )
);

function injectBeamerScript() {
  const BEAMER_SCRIPT_ID = 'beamer_script';
  // Avoid injecting the script multiple times
  if (document.getElementById(BEAMER_SCRIPT_ID)) {
    return null;
  }

  const script = document.createElement('script');
  script.id = BEAMER_SCRIPT_ID;
  script.type = 'text/javascript';
  script.src = 'https://app.getbeamer.com/js/beamer-embed.js';
  script.defer = 'defer';
  document.head.appendChild(script);
  return script;
}

export default {
  name: 'App',
  mixins: [monitorWebsocketConnectionMixin],
  components: {
    FeatureFlag,
    EventTriggeredLazyDialogHost
  },
  data() {
    return {
      isChatWindowOpen: false,
      unreadMsgCount: 0,
      isMobile: false,
      deviceAgnosticRoutes: ['login', 'register', 'resetPassword']
    };
  },
  computed: {
    ...mapState('navigation', {
      pageTitle: state => state.pageTitle
    }),
    shouldRenderMobile() {
      return this.isMobile && !this.deviceAgnosticRoutes.includes(this.$route.name);
    },
    orgRequired() {
      let shouldCheckOrg = !this.$route.meta.bypassOrgCheck;
      let notInternalUser = this.hasUser && !isInternalUser(this.$me);
      let hasNoOrg = this.hasUser && !this.$me.orgId;

      return shouldCheckOrg && (!this.hasUser || (notInternalUser && hasNoOrg));
    },
    hasUser() {
      return this.$me?.id;
    },
    showDisabledOrgAlert() {
      return this.$org && Boolean(this.$me?.orgId) && this.$org.isActive === false;
    },
    isInternalUser() {
      return isInternalUser(this.$me) || sessionStorage.getItem('login_origin') === 'loginAs';
    },
    isConfirmationNeeded() {
      return (
        !!this.$me &&
        !this.isInternalUser &&
        (!this.$me.tcConfirmedAt ||
          DateTime.fromISO(this.$me.tcConfirmedAt) < config.terms_conditions_last_update)
      );
    }
  },
  async mounted() {
    this.isMobile = isMobile();
    if (this.isMobile) {
      document.body.classList.add('is-mobile');
    }
    this.$store.commit('Settings/setSettingsMetadata', EntitySettings);
    this.$nextTick(() => {
      this.$store.dispatch('Auth/setInitialAccessToken');
    });

    this.$eventHub.$on('heartbeat', payload =>
      this.$store.dispatch('App/verifyNeutronVersion', payload)
    );

    const localSettings = this.$loadObjectFromLocalStorage('local_settings');
    this.$store.commit('Settings/setLocalSettings', localSettings);
    const { addSubspaceLoggingEventListener } = useSettings();
    if (localSettings.logSubspace) {
      addSubspaceLoggingEventListener();
    }
  },
  methods: {
    getRouteTitle,
    kebabCase,
    _enableLauncherBtn() {
      // Enable the launcher button, initially hidden, because it's the only way to minimize the chat
      const iframeLauncher = document.querySelector('iframe#launcher');
      if (iframeLauncher) {
        iframeLauncher.style.display = 'block';
      }
    },
    _openChatWindow() {
      if (window.zE && window.zE?.activate) {
        window.zE.activate();
        // Make sure the messenger is enabled
        // once we disabled it on some pages (TV Mode)
        this._enableLauncherBtn();
        this.$nextTick(() => {
          window.zE('messenger', 'show');
        });
      } else if (typeof zE === 'function') {
        this._enableLauncherBtn();
        this.$nextTick(() => {
          window.zE('messenger', 'show');
          window.zE('messenger', 'open');
        });
      } else {
        this.notify(
          'We are having trouble connecting to Support Chat. Please try again later.',
          'error'
        );
      }
    },
    _setupZendeskMessenger() {
      const isUsingMessengerApi = typeof window.zE === 'function';
      if (isUsingMessengerApi && Boolean(this.$route?.meta?.hideSidebar) === false) {
        window.zE('messenger', 'close');
        window.zE('messenger', 'hide');
        window.zE('messenger:on', 'unreadMessages', number => {
          this.unreadMsgCount = number;
        });
        window.zE('messenger:on', 'close', () => {
          window.zE('messenger', 'hide');
          this.isChatWindowOpen = false;
        });
      } else if (isUsingMessengerApi) {
        window.zE('messenger', 'show');
      }
    },
    _bindZendeskLoadListener() {
      if (typeof window.zE !== 'undefined') {
        this._setupZendeskMessenger();
      } else {
        window.addEventListener(
          'load',
          () => {
            this._setupZendeskMessenger();
          },
          {
            once: true
          }
        );
      }
    }
  },
  watch: {
    $route(newVal, oldVal) {
      if (!this.isMobile) {
        this._bindZendeskLoadListener();
      } else {
        this.$store.commit('App/setMobilePageTitle', this.$route.meta.title);
      }
      const shouldUpdateRoute =
        newVal.query.appointmentId &&
        newVal.query.appointmentId !== oldVal.query?.appointmentId &&
        newVal.query.appointmentId !== this.$route.query.appointmentId;

      if (shouldUpdateRoute) {
        this.handleAppointmentQueryString(this.$route.path, newVal.query.appointmentId);
      }
    },
    $me(newVal) {
      if (
        !this.shouldRenderMobile &&
        !isInternalUser(this.$me) &&
        !isLoggedInAsAnotherUser(this.$me) &&
        this.$route.path !== '/appointments/tv' &&
        newVal
      ) {
        window.beamer_config = {
          product_id: import.meta.env.VITE_BEAMER_PRODUCT_ID,
          top: 14,
          right: 20,
          user_id: newVal.id,
          user_firstname: newVal.firstName,
          user_lastname: newVal.lastName,
          user_email: newVal.email,
          user_role: newVal.role,
          user_org_id: newVal.orgId
        };
        injectBeamerScript();
      }
    },
    '$i18n.locale'() {
      // Set page title when locale changes so it doesn't get "stuck" in initially selected language
      routeHelpers.setPageTitle(this.$store, this.$route, config.application_name ?? 'Opendock');
    }
  },
  setup() {
    const { locale } = useI18n({ useScope: 'global' });
    const { current } = useLocale();
    const {
      getBaseLanguageCodeFromLocale,
      getLocaleFromLocalStorage,
      getReadableLanguageFromLocale
    } = useLocaleHelpers();
    const { meRef } = useAuth();
    const mixpanel = useMixpanel();

    watch(
      locale,
      newLocale => {
        current.value = getBaseLanguageCodeFromLocale(newLocale);
        mixpanel.updateApplicationLanguageProperty(getReadableLanguageFromLocale(newLocale));
      },
      { immediate: true }
    );

    watch(
      () => meRef.value?.id,
      id => {
        if (id) {
          const storedLocale = getLocaleFromLocalStorage(id);
          current.value = getBaseLanguageCodeFromLocale(storedLocale);
          locale.value = storedLocale;
        }
      }
    );

    return {
      EventTriggeredRemoveCarrierContactDialog,
      EventTriggeredCreateCarrierCompanyDialog,
      EventTriggeredCreateCarrierContactDialog,
      EventTriggeredCreateMoveTaskDrawer,
      EventTriggeredCreateTaskDrawer,
      EventTriggeredEditTaskDrawer,
      EventTriggeredLimitsOverviewDrawer
    };
  }
};
</script>

<style lang="scss">
.is-mobile-content {
  margin-top: 75px;
}
.is-mobile {
  .dialog-content {
    padding: 0 !important;
  }

  .mobile-nav-dialog-body {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}
</style>
